import { NetClient } from "../net/net-client";
import { OnlineMatch, type OnlineStatus } from "../net/online-match";
import {
	type ClientMessage,
	cleanPilotName,
	type EndReason,
	type ErrorCode,
	isServerAddress,
	type OnlineMode,
	PILOT_NAME_MAX,
	PROTOCOL_VERSION,
	type RematchSetup,
	type ServerMessage,
	setupOf,
	socketUrl,
} from "../net/protocol";
import type { MapDef, MatchRules } from "../sim";
import { menuScreen } from "../ui/components";
import { el } from "../ui/dom";
import type { Screen, ScreenManager } from "../ui/screen-manager";
import type { MapKey } from "./assets";
import { loadSettings, ONLINE_SERVER_MAX, saveSettings } from "./settings";

/** What the online flow needs from the game flow. */
export interface OnlineHost {
	readonly ui: ScreenManager;
	/** Starts (or restarts, after a rejoin) the match scene for an online match. */
	startOnline(match: OnlineMatch): void;
	showMainMenu(): void;
	/** Map → rules screens of the local flow; `start` receives the choice. */
	chooseMatch(
		players: number,
		start: (
			map: MapKey,
			rules: MatchRules,
			customMap: MapDef | undefined,
			mode: OnlineMode,
		) => void,
	): Screen;
	/** Short description of a rematch setup, e.g. "Asteroid belt · Modern rules · King of the Hill". */
	describeSetup(setup: RematchSetup): string;
}

const ERROR_TEXT: Record<ErrorCode, string> = {
	"bad-message": "The server didn't understand this game version.",
	version: "This game and the server are different versions. Reload the page to update.",
	"server-full": "The server is full right now. Try again in a little while.",
	"room-not-found": "No room with that code. Check the code, or ask your friend for a new one.",
	"room-full": "That room already has two players.",
	"already-in-room": "You are already in a room.",
	"rejoin-failed": "The match is over or your seat was given up.",
};

const END_TEXT: Record<EndReason, string> = {
	"peer-left": "Your opponent left the match.",
	"peer-timeout": "Your opponent's connection was lost and didn't come back.",
	shutdown: "The server was restarted.",
	"too-long": "The match reached the server's time limit.",
};

/** Label of the online game over's rematch button. */
export const REMATCH = "Rematch";
/** Label of the host's button for a rematch on another map, rules or mode (I-013). */
export const CHANGE_REMATCH = "Change map & rules";
const CODE_LENGTH = 4;
const REJOIN_TRIES = 8;
const REJOIN_EVERY_MS = 2500;

/** Median of three pings to the server in ms, or null if it doesn't answer in time. */
async function measureRtt(client: NetClient): Promise<number | null> {
	const samples: number[] = [];
	for (let id = 1; id <= 3; id++) {
		const rtt = await new Promise<number | null>((resolve) => {
			const sent = performance.now();
			const timer = setTimeout(() => {
				off();
				resolve(null);
			}, 1000);
			const off = client.onMessage((m) => {
				if (m.t === "pong" && m.id === -id) {
					clearTimeout(timer);
					off();
					resolve(performance.now() - sent);
				}
			});
			client.send({ t: "ping", id: -id });
		});
		if (rtt !== null) samples.push(rtt);
	}
	samples.sort((a, b) => a - b);
	return samples.length > 0 ? (samples[Math.floor(samples.length / 2)] ?? null) : null;
}

/** Native share sheet (phones); desktop browsers mostly lack it. */
function canShare(): boolean {
	return typeof navigator.share === "function";
}

/** Room codes: upper case, without the look-alikes the server never uses (0 O 1 I). */
/** The Online menu's server item: this site, or the custom relay's host. */
function serverLabel(): string {
	const custom = loadSettings().onlineServer;
	return custom
		? `Server: ${new URL(socketUrl(window.location.href, custom)).host}`
		: "Server: this site";
}

export function cleanCode(text: string): string {
	return text
		.toUpperCase()
		.replace(/[^A-Z2-9]/g, "")
		.replace(/[OI]/g, "")
		.slice(0, CODE_LENGTH);
}

/** Invite link for a room: this page with `?join=CODE`. */
export function inviteLink(pageUrl: string, code: string): string {
	const url = new URL(pageUrl);
	url.search = `?join=${code}`;
	url.hash = "";
	return url.toString();
}

/**
 * Online play screens and connection handling: create or join a room, the lobby, the in-match
 * status line, automatic rejoin after a lost connection, and leaving.
 */
export class OnlineFlow {
	private match: OnlineMatch | null = null;
	/** Lobby connection (before the match starts); closed when backing out. */
	private pending: NetClient | null = null;
	private statusBar: HTMLElement | null = null;
	private statusTimer: ReturnType<typeof setInterval> | null = null;
	/** The lobby's "2 of 4 pilots here" line (party rooms). */
	private lobbyCount: HTMLElement | null = null;
	/** Set after game over and when leaving: status changes no longer interrupt. */
	private quiet = false;
	/** The Online menu's server item, relabelled when the address changes. */
	private serverButton: HTMLButtonElement | null = null;

	constructor(private readonly host: OnlineHost) {}

	get current(): OnlineMatch | null {
		return this.match;
	}

	menu(): Screen {
		// The pilot name is what people expect to type here; the server address (which "Test" once
		// ended up in, I-023) has its own screen.
		const name = el("input", {
			type: "text",
			id: "online-name",
			maxlength: PILOT_NAME_MAX,
			value: loadSettings().pilotNames[0] ?? "",
			placeholder: "your ship's colour",
			autocomplete: "off",
			spellcheck: false,
		});
		name.addEventListener("input", () => {
			const settings = loadSettings();
			const pilotNames = [...settings.pilotNames];
			pilotNames[0] = cleanPilotName(name.value);
			saveSettings({ ...settings, pilotNames });
		});
		const screen = menuScreen({
			title: "Online",
			subtitle: "Duel a friend over the internet",
			className: "online-menu",
			content: el(
				"div",
				{ class: "field" },
				el("label", { for: "online-name" }, "Your name"),
				name,
			),
			back: () => this.host.ui.pop(),
			items: [
				{
					label: "Create room",
					hint: "Pick room size, mode, map and rules, then share the room code or invite link.",
					onSelect: () => this.host.ui.push(this.roomSize()),
				},
				{
					label: "Join room",
					hint: "Enter the code your friend sees in their lobby.",
					onSelect: () => this.host.ui.push(this.joinScreen()),
				},
				{
					label: serverLabel(),
					hint: "Only for playing through another relay server. Leave it on this site.",
					onSelect: () => this.host.ui.push(this.serverScreen()),
				},
			],
		});
		this.serverButton = screen.el.querySelectorAll<HTMLButtonElement>("[role=menuitem]")[2] ?? null;
		return screen;
	}

	private setServer(address: string): void {
		saveSettings({ ...loadSettings(), onlineServer: address });
		if (this.serverButton) this.serverButton.textContent = serverLabel();
	}

	/** Custom relay address (advanced): validated before it is saved, blank = this site. */
	private serverScreen(): Screen {
		const input = el("input", {
			type: "text",
			id: "online-server",
			maxlength: ONLINE_SERVER_MAX,
			value: loadSettings().onlineServer,
			placeholder: "this site",
			autocomplete: "off",
			spellcheck: false,
			"aria-describedby": "online-server-error",
			"data-autofocus": true,
		});
		const error = el("p", { class: "field-error", id: "online-server-error", role: "alert" });
		const store = (address: string) => {
			this.setServer(address);
			this.host.ui.pop();
		};
		const save = () => {
			const address = input.value.trim();
			if (!isServerAddress(address)) {
				error.textContent =
					"That isn't a server address. Use a host name like play.example.com or nas.lan:3070, or leave it blank.";
				input.setAttribute("aria-invalid", "true");
				return;
			}
			store(address);
		};
		input.addEventListener("input", () => {
			error.textContent = "";
			input.removeAttribute("aria-invalid");
		});
		input.addEventListener("keydown", (ev) => {
			if (ev.key === "Enter") {
				ev.preventDefault();
				ev.stopPropagation(); // not also a click on the next screen (I-007)
				save();
			}
		});
		return menuScreen({
			title: "Server",
			subtitle: "Leave blank to play on this site",
			className: "online-server",
			content: el(
				"div",
				{ class: "field" },
				el("label", { for: "online-server" }, "Server address"),
				input,
				error,
			),
			back: () => this.host.ui.pop(),
			items: [
				{ label: "Save", onSelect: save },
				{ label: "Use this site", onSelect: () => store("") },
			],
		});
	}

	/** Joins a room straight away (invite link `?join=CODE`). */
	join(code: string): void {
		void this.connectAndSend({ t: "join", v: PROTOCOL_VERSION, code }, `Joining room ${code}…`);
	}

	/** Leaves the running match (the opponent is told) and stops showing its status. */
	leave(): void {
		this.quiet = true;
		this.match?.leave();
		this.match = null;
		this.removeStatusBar();
	}

	/** Game over: keep the connection for the stats screen, but ignore the opponent leaving. */
	finished(): void {
		this.quiet = true;
		this.removeStatusBar();
	}

	/** Back to the menus: drop any lobby or match connection. */
	reset(): void {
		this.pending?.close();
		this.pending = null;
		this.leave();
	}

	/** Room size (P11.3): a duel, or a party room for 3–4 pilots; then mode, map and rules. */
	private roomSize(): Screen {
		const pick = (players: number) => () =>
			this.host.ui.push(
				this.host.chooseMatch(players, (map, rules, customMap, mode) =>
					this.create(map, rules, customMap, mode, players),
				),
			);
		return menuScreen({
			title: "Room size",
			subtitle: "How many pilots fly in this room",
			back: () => this.host.ui.pop(),
			items: [
				{
					label: "2 pilots",
					hint: "A duel (also King of the Hill or Capture the Flag).",
					onSelect: pick(2),
				},
				{
					label: "3 pilots",
					hint: "Free-for-all: share the code with two friends.",
					onSelect: pick(3),
				},
				{
					label: "4 pilots",
					hint: "Free-for-all: share the code with three friends.",
					onSelect: pick(4),
				},
			],
		});
	}

	private create(
		map: MapKey,
		rules: MatchRules,
		customMap: MapDef | undefined,
		mode: OnlineMode,
		players = 2,
	): void {
		void this.connectAndSend(
			{
				t: "create",
				v: PROTOCOL_VERSION,
				map,
				rules,
				mode,
				...(customMap ? { customMap } : {}),
				...(players > 2 ? { players } : {}),
			},
			"Creating room…",
		);
	}

	private joinScreen(): Screen {
		const input = el("input", {
			type: "text",
			id: "room-code",
			maxlength: CODE_LENGTH,
			autocomplete: "off",
			spellcheck: false,
			"data-autofocus": true,
		});
		const submit = () => {
			const code = cleanCode(input.value);
			if (code.length === CODE_LENGTH) this.join(code);
		};
		input.addEventListener("input", () => {
			input.value = cleanCode(input.value);
		});
		input.addEventListener("keydown", (ev) => {
			if (ev.key === "Enter") {
				ev.preventDefault();
				ev.stopPropagation(); // not also a click on the next screen (I-007)
				submit();
			}
		});
		return menuScreen({
			title: "Join room",
			className: "join-room",
			content: el("div", { class: "field" }, el("label", { for: "room-code" }, "Room code"), input),
			back: () => this.host.ui.pop(),
			items: [{ label: "Join", onSelect: submit }],
		});
	}

	/** Connects, sends `first` and follows the server's answers until the match starts. */
	private async connectAndSend(
		first: Extract<ClientMessage, { t: "create" | "join" }>,
		busy: string,
	): Promise<void> {
		const name = loadSettings().pilotNames[0];
		if (name) first = { ...first, name };
		this.reset();
		this.quiet = false;
		let cancelled = false;
		const waiting = this.message(busy, "", [
			{
				label: "Cancel",
				onSelect: () => {
					cancelled = true;
					this.reset();
					this.host.ui.remove(waiting);
				},
			},
		]);
		this.host.ui.push(waiting);
		const custom = loadSettings().onlineServer;
		let client: NetClient;
		try {
			client = await NetClient.connect(socketUrl(window.location.href, custom));
		} catch {
			if (cancelled) return;
			if (!custom) {
				this.fail(waiting, "Can't reach the online server. Check your connection and try again.");
				return;
			}
			const host = new URL(socketUrl(window.location.href, custom)).host;
			this.fail(
				waiting,
				`Can't reach the online server at ${host}. Check your connection, or go back to this site's server.`,
				[
					{
						label: "Use this site's server",
						onSelect: () => {
							this.setServer("");
							this.host.ui.pop();
						},
					},
				],
			);
			return;
		}
		if (cancelled) {
			client.close();
			return;
		}
		this.pending = client;
		// Round trip to the server: the server picks the input delay from both players' (P10.5).
		const rtt = await measureRtt(client);
		if (cancelled || this.pending !== client) return;
		let lobby: Screen | null = null;
		let lobbyCode = first.t === "join" ? first.code : "";
		const off = client.onMessage((m: ServerMessage) => {
			switch (m.t) {
				case "created":
					lobbyCode = m.code;
					lobby = this.lobby(m.code);
					this.host.ui.remove(waiting);
					this.host.ui.push(lobby);
					break;
				case "lobby":
					// Party rooms (P11.3): guests wait in the lobby too, until every seat is taken.
					if (!lobby && lobbyCode) {
						lobby = this.lobby(lobbyCode);
						this.host.ui.remove(waiting);
						this.host.ui.push(lobby);
					}
					if (this.lobbyCount) {
						this.lobbyCount.textContent = `${m.joined} of ${m.size} pilots here`;
						this.lobbyCount.dataset.joined = String(m.joined);
					}
					break;
				case "start":
					off();
					this.pending = null;
					this.begin(new OnlineMatch(client, setupOf(m), m.player, m.token));
					break;
				case "error":
					off();
					this.reset();
					this.fail(lobby ?? waiting, ERROR_TEXT[m.code]);
					break;
				case "ended":
					off();
					this.reset();
					this.fail(lobby ?? waiting, "Nobody joined in time. Create a new room to try again.");
					break;
				default:
					break;
			}
		});
		client.onClose(() => {
			if (this.pending === client) {
				this.pending = null;
				this.fail(lobby ?? waiting, "The connection to the server was lost.");
			}
		});
		client.send(rtt === null ? first : { ...first, rtt });
	}

	private lobby(code: string): Screen {
		const link = inviteLink(window.location.href, code);
		const share = async () => {
			try {
				if (canShare())
					await navigator.share({
						title: "Gravity",
						text: `Join my Gravity match: room ${code}`,
						url: link,
					});
				else await navigator.clipboard.writeText(link);
				copied.textContent = canShare() ? "" : "Invite link copied.";
			} catch {
				copied.textContent = "Copy the link above by hand.";
			}
		};
		const copied = el("p", { class: "menu-hint", "aria-live": "polite" });
		this.lobbyCount = el("p", { class: "online-text lobby-count", "aria-live": "polite" });
		return menuScreen({
			title: "Waiting for opponents",
			subtitle: "Share the code or the invite link",
			className: "online-lobby",
			content: el(
				"div",
				{},
				el("p", { class: "room-code", "data-room-code": code }, code),
				el("p", { class: "invite-link" }, link),
				this.lobbyCount,
				copied,
			),
			back: () => this.cancelLobby(),
			items: [
				{
					label: canShare() ? "Share invite" : "Copy invite link",
					onSelect: () => void share(),
				},
				{ label: "Cancel", onSelect: () => this.cancelLobby() },
			],
		});
	}

	private cancelLobby(): void {
		this.reset();
		this.host.ui.pop();
	}

	/**
	 * Online game over (P10.3): wires the screen's Rematch button. Asking waits for the
	 * opponent; their request and their leaving show up as a note above the buttons. The host can
	 * pick another map, rules or mode first (I-013): the others see the choice and must accept it.
	 */
	attachGameOver(root: HTMLElement): void {
		const match = this.match;
		const buttons = [...root.querySelectorAll("button")];
		const button = buttons.find((b) => b.textContent === REMATCH);
		const change = buttons.find((b) => b.textContent === CHANGE_REMATCH);
		if (!match || !button) return;
		const note = el("p", { class: "online-text rematch-note", "aria-live": "polite" });
		root.querySelector(".menu-list")?.before(note);
		const waiting = () => {
			button.disabled = true;
			button.textContent = "Waiting for opponent…";
		};
		const closed = (text: string) => {
			note.textContent = text;
			button.disabled = true;
			button.textContent = REMATCH;
			if (change) change.disabled = true;
		};
		match.onRematchWanted = (setup) => {
			if (setup) {
				// The host changed the match: an earlier request was for the old one.
				button.disabled = false;
				button.textContent = REMATCH;
				note.textContent = `The host picked a new match: ${this.host.describeSetup(setup)}.`;
				return;
			}
			if (!match.rematchRequested) note.textContent = "Your opponent wants a rematch.";
		};
		match.onStatus = (status) => {
			if (status.kind === "ended") closed(END_TEXT[status.reason]);
			if (status.kind === "disconnected") closed("The connection to the server was lost.");
		};
		button.addEventListener("click", () => {
			match.requestRematch();
			waiting();
			note.textContent = "";
		});
		change?.addEventListener("click", () => {
			this.host.ui.push(
				this.host.chooseMatch(match.setup.players ?? 2, (map, rules, customMap, mode) => {
					const setup: RematchSetup = { map, rules, mode, ...(customMap ? { customMap } : {}) };
					// Back to this game over screen, under the mode, map and rules screens.
					while (this.host.ui.top && this.host.ui.top.el !== root) this.host.ui.pop();
					if (button.disabled && button.textContent === REMATCH) return; // closed meanwhile
					match.requestRematch(setup);
					waiting();
					note.textContent = `Next: ${this.host.describeSetup(setup)}.`;
				}),
			);
		});
	}

	private begin(match: OnlineMatch): void {
		this.match = match;
		this.quiet = false;
		match.onStatus = (status) => this.onStatus(match, status);
		match.onRematch = (next) => this.begin(next);
		this.host.startOnline(match);
		this.showStatusBar(match);
	}

	private onStatus(match: OnlineMatch, status: OnlineStatus): void {
		if (this.quiet || match !== this.match) return;
		if (status.kind === "ended") {
			this.finished();
			this.host.ui.reset(
				this.message("Match ended", END_TEXT[status.reason], [
					{ label: "Main menu", onSelect: () => this.host.showMainMenu() },
				]),
			);
			document.body.dataset.screen = "online-ended";
		} else if (status.kind === "disconnected") {
			void this.rejoin(match);
		}
	}

	/** The connection dropped mid-match: try to take the seat back while the server keeps it. */
	private async rejoin(old: OnlineMatch): Promise<void> {
		const url = socketUrl(window.location.href, loadSettings().onlineServer);
		for (let attempt = 0; attempt < REJOIN_TRIES && this.match === old && !this.quiet; attempt++) {
			await new Promise((resolve) => setTimeout(resolve, attempt === 0 ? 300 : REJOIN_EVERY_MS));
			let client: NetClient;
			try {
				client = await NetClient.connect(url, 4000);
			} catch {
				continue;
			}
			const answer = await new Promise<ServerMessage | null>((resolve) => {
				const off = client.onMessage((m) => {
					if (m.t === "resume" || m.t === "error") {
						off();
						resolve(m);
					}
				});
				client.onClose(() => resolve(null));
				client.send({ t: "rejoin", v: PROTOCOL_VERSION, code: old.setup.code, token: old.token });
			});
			if (answer?.t === "resume") {
				old.dispose();
				this.begin(
					new OnlineMatch(client, setupOf(answer), answer.player, answer.token, answer.inputs),
				);
				return;
			}
			client.close();
			if (answer?.t === "error") break;
		}
		if (this.match !== old || this.quiet) return;
		this.leave();
		this.host.ui.reset(
			this.message("Connection lost", "Couldn't get back into the match.", [
				{ label: "Main menu", onSelect: () => this.host.showMainMenu() },
			]),
		);
		document.body.dataset.screen = "online-ended";
	}

	private showStatusBar(match: OnlineMatch): void {
		this.removeStatusBar();
		const bar = el("div", { class: "online-status", role: "status" });
		document.getElementById("ui-root")?.append(bar);
		this.statusBar = bar;
		const render = () => {
			const s = match.current;
			const ping = match.rtt === null ? "" : ` · ${Math.round(match.rtt)} ms`;
			let text = `Online · room ${match.setup.code}${ping}`;
			if (s.kind === "waiting") text = "Waiting for your opponent…";
			if (s.kind === "peer-left") {
				text = `Opponent disconnected · waiting ${Math.max(0, Math.ceil((s.until - Date.now()) / 1000))} s`;
			}
			if (s.kind === "disconnected") text = "Connection lost · reconnecting…";
			if (match.desyncTick !== null) text += " · out of sync!";
			bar.textContent = text;
			bar.dataset.status = s.kind;
		};
		render();
		this.statusTimer = setInterval(render, 500);
	}

	private removeStatusBar(): void {
		if (this.statusTimer !== null) clearInterval(this.statusTimer);
		this.statusTimer = null;
		this.statusBar?.remove();
		this.statusBar = null;
	}

	private message(
		title: string,
		text: string,
		items: Parameters<typeof menuScreen>[0]["items"],
	): Screen {
		return menuScreen({
			title,
			className: "online-message",
			...(text ? { content: el("p", { class: "online-text" }, text) } : {}),
			back: () => items[0]?.onSelect(),
			items,
		});
	}

	private fail(
		replace: Screen,
		text: string,
		extra: Parameters<typeof menuScreen>[0]["items"] = [],
	): void {
		const screen = this.message("Online", text, [
			{ label: "Back", onSelect: () => this.host.ui.remove(screen) },
			...extra,
		]);
		this.host.ui.remove(replace);
		this.host.ui.push(screen);
	}
}
